import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import { convertDistance, convertSpeed, convertElevation, getUnits } from '../pages/WorkoutAnalysisV2';
import { exportGpx } from '../api/client';

const GpxExportAction = ({ workoutData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const chunks = workoutData?.chunks || [];

  const [units, setUnits] = useState(getUnits());
  const [selectedMetrics, setSelectedMetrics] = useState(['altitude', 'speed']);

  // Selection state (what's shown on the chart)
  const [startDistance, setStartDistance] = useState(0);
  const [endDistance, setEndDistance] = useState(0);
  const [maxDistance, setMaxDistance] = useState(0);
  const [selectionStep, setSelectionStep] = useState(0); // 0=no selection, 1=start selected, 2=complete

  // Input field values (strings for proper editing - not applied to chart until user clicks Apply)
  const [startDistanceInput, setStartDistanceInput] = useState('0.00');
  const [endDistanceInput, setEndDistanceInput] = useState('0.00');
  const [hasStartPendingChanges, setHasStartPendingChanges] = useState(false);
  const [hasEndPendingChanges, setHasEndPendingChanges] = useState(false);

  const pendingChanges = hasStartPendingChanges || hasEndPendingChanges;

  // Calculate distance from start and end
  const distance = Math.max(0, endDistance - startDistance);

  // Listen for unit changes
  useEffect(() => {
    const handleUnitsChange = (e) => {
      setUnits(e.detail || getUnits());
    };
    window.addEventListener('unitsChanged', handleUnitsChange);
    return () => {
      window.removeEventListener('unitsChanged', handleUnitsChange);
    };
  }, []);

  // Calculate total distance for initial values
  useEffect(() => {
    if (chunks.length > 0) {
      const totalDistanceMeters = chunks.reduce((sum, chunk) => sum + chunk.distance, 0);
      const totalDistanceConverted = convertDistance(totalDistanceMeters, units);
      setMaxDistance(totalDistanceConverted);
      setStartDistance(0);
      setEndDistance(totalDistanceConverted);
      setStartDistanceInput('0.00');
      setEndDistanceInput(totalDistanceConverted.toFixed(2));
      setSelectionStep(2); // Show full route by default
    }
  }, [chunks, units]);

  const chartLabels = {
    elevationLabel: t('altitudeLineName', 'Altitude'),
    speedLabel: t('speedLineName', 'Speed'),
    gradientLabel: t('gradientLineName', 'Gradient'),
    powerLabel: t('powerLineName', 'Power'),
    distanceLabel: `${t('distance', 'Distance')} (${units === 'imperial' ? 'mi' : 'km'})`
  };

  // Prepare chart data
  const chartData = (() => {
    let cumulativeDistanceMeters = 0;

    return chunks.map((chunk) => {
      cumulativeDistanceMeters += chunk.distance;

      return {
        distance: parseFloat(convertDistance(cumulativeDistanceMeters, units).toFixed(2)),
        altitude: parseFloat(convertElevation(chunk.avg_altitude, units).toFixed(1)),
        speed: parseFloat(convertSpeed(chunk.avg_speed, units).toFixed(1)),
        gradient: parseFloat(chunk.avg_gradient.toFixed(2)),
        power: parseFloat(chunk.avg_power.toFixed(0))
      };
    });
  })();

  const handleChartClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedDistance = data.activePayload[0].payload.distance;

      if (selectionStep === 0 || selectionStep === 2) {
        // First click or reset - set start point
        setStartDistance(clickedDistance);
        setStartDistanceInput(clickedDistance.toFixed(2));
        setSelectionStep(1);
        setHasStartPendingChanges(false);
        setHasEndPendingChanges(false);
      } else if (selectionStep === 1) {
        // Second click - set end point
        if (clickedDistance > startDistance) {
          setEndDistance(clickedDistance);
          setEndDistanceInput(clickedDistance.toFixed(2));
          setSelectionStep(2);
        } else {
          // Clicked before start, swap them
          setEndDistance(startDistance);
          setEndDistanceInput(startDistance.toFixed(2));
          setStartDistance(clickedDistance);
          setStartDistanceInput(clickedDistance.toFixed(2));
          setSelectionStep(2);
        }
        setHasStartPendingChanges(false);
        setHasEndPendingChanges(false);
      }
    }
  };

  const handleStartDistanceChange = (value) => {
    setStartDistanceInput(value);
    setHasStartPendingChanges(true);
  };

  const handleEndDistanceChange = (value) => {
    setEndDistanceInput(value);
    setHasEndPendingChanges(true);
  };

  // Helper function to find the closest distance value in the chart data
  const findClosestDataPoint = (targetDistance) => {
    if (chartData.length === 0) return targetDistance;

    let closest = chartData[0].distance;
    let minDiff = Math.abs(targetDistance - closest);

    for (let i = 1; i < chartData.length; i++) {
      const diff = Math.abs(targetDistance - chartData[i].distance);
      if (diff < minDiff) {
        minDiff = diff;
        closest = chartData[i].distance;
      }
    }

    return closest;
  };

  const handleApplyManualSelection = () => {
    const parsedStart = parseFloat(startDistanceInput);
    const parsedEnd = parseFloat(endDistanceInput);

    if (isNaN(parsedStart) || isNaN(parsedEnd)) {
      alert('Please enter valid numbers for both start and end distances.');
      return;
    }

    // Validate and clamp values
    const clampedStart = Math.max(0, Math.min(parsedStart, maxDistance));
    const clampedEnd = Math.max(clampedStart, Math.min(parsedEnd, maxDistance));

    // Snap to closest data points
    const newStart = findClosestDataPoint(clampedStart);
    const newEnd = findClosestDataPoint(clampedEnd);

    // Update chart selection
    setStartDistance(newStart);
    setEndDistance(newEnd);
    setSelectionStep(2);
    setHasStartPendingChanges(false);
    setHasEndPendingChanges(false);

    // Format the input fields with snapped values
    setStartDistanceInput(newStart.toFixed(2));
    setEndDistanceInput(newEnd.toFixed(2));
  };

  const handleResetSelection = () => {
    setStartDistance(0);
    setEndDistance(maxDistance);
    setStartDistanceInput('0.00');
    setEndDistanceInput(maxDistance.toFixed(2));
    setSelectionStep(2);
    setHasStartPendingChanges(false);
    setHasEndPendingChanges(false);
  };

  const handleExportGpx = async () => {
    if (!workoutData?.analysis_session_id) {
      alert('Error: Analysis session ID not found. Please re-analyze your workout.');
      return;
    }

    if (selectionStep !== 2) {
      alert('Please select both start and end points before exporting.');
      return;
    }

    // Convert distances back to meters for the API
    const startDistanceMeters = units === 'imperial' ? startDistance * 1609.34 : startDistance * 1000;
    const endDistanceMeters = units === 'imperial' ? endDistance * 1609.34 : endDistance * 1000;

    try {
      // Call the export API using the API client
      const response = await exportGpx({
        analysisSessionId: workoutData.analysis_session_id,
        startDistance: startDistanceMeters,
        endDistance: endDistanceMeters
      });

      // Get the filename from the response headers
      // Note: axios lowercases all header names
      console.log('All response headers:', response.headers);
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      console.log('Content-Disposition header:', contentDisposition);
      let filename = 'mkpace_bike_export.gpx';
      if (contentDisposition) {
        // Match filename with or without quotes
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        console.log('Regex matches:', matches);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      console.log('Final filename:', filename);

      // Download the file
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('GPX exported successfully:', filename);
    } catch (error) {
      console.error('Error exporting GPX:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error exporting GPX: ${errorMessage}`);
    }
  };

  if (!chunks || chunks.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {t('noWorkoutDataForGpxExport', 'No workout data available for GPX export.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <Typography variant="h5" color={colors.grey[100]} mb={3}>
        {t('exportModifiedGpx', 'Export Modified GPX')}
      </Typography>

      {/* Selection Controls */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.grey[800],
          borderRadius: '8px'
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="grid" gridTemplateColumns="1fr 1fr 100px" gap={2} alignItems="end">
            <TextField
              label={`${t('startDistance', 'Start')} (${units === 'imperial' ? 'mi' : 'km'})`}
              type="number"
              value={startDistanceInput}
              onChange={(e) => handleStartDistanceChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyManualSelection();
                }
              }}
              size="small"
              inputProps={{
                step: 0.5,
                min: 0,
                max: maxDistance
              }}
              sx={{
                '& .MuiInputLabel-root': { color: colors.grey[300] },
                '& .MuiInputBase-input': { color: colors.grey[100] },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasStartPendingChanges ? colors.warnings.orange : colors.grey[600]
                }
              }}
            />
            <TextField
              label={`${t('endDistance', 'End')} (${units === 'imperial' ? 'mi' : 'km'})`}
              type="number"
              value={endDistanceInput}
              onChange={(e) => handleEndDistanceChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyManualSelection();
                }
              }}
              size="small"
              inputProps={{
                step: 0.5,
                min: 0,
                max: maxDistance
              }}
              sx={{
                '& .MuiInputLabel-root': { color: colors.grey[300] },
                '& .MuiInputBase-input': { color: colors.grey[100] },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasEndPendingChanges ? colors.warnings.orange : colors.grey[600]
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyManualSelection}
              disabled={!pendingChanges}
              size="small"
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.blueAccent[700]
                },
                '&:disabled': {
                  backgroundColor: colors.grey[700],
                  color: colors.grey[500]
                }
              }}
            >
              {t('apply', 'Apply')}
            </Button>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" color={colors.grey[400]}>
                {t('segmentDistance', 'Segment Distance')}
              </Typography>
              <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="bold">
                {distance.toFixed(2)} {units === 'imperial' ? 'mi' : 'km'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={handleResetSelection}
              size="small"
              sx={{
                color: colors.grey[100],
                borderColor: colors.grey[600],
                '&:hover': {
                  borderColor: colors.grey[400],
                  backgroundColor: colors.grey[800]
                }
              }}
            >
              {t('resetToFullRoute', 'Reset to Full Route')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Chart Controls */}
      <Box mb={2}>
        <FormControl sx={{ minWidth: isMobile ? '100%' : 300, width: isMobile ? '100%' : 'auto' }}>
          <InputLabel sx={{ color: colors.grey[300] }}>{t('selectMetrics', 'Select Metrics')}</InputLabel>
          <Select
            multiple
            value={selectedMetrics}
            onChange={(e) => setSelectedMetrics(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label={t('selectMetrics', 'Select Metrics')} />}
            renderValue={(selected) => selected.map(m =>
              m === 'altitude' ? chartLabels.elevationLabel :
              m === 'speed' ? chartLabels.speedLabel :
              m === 'gradient' ? chartLabels.gradientLabel :
              chartLabels.powerLabel
            ).join(', ')}
            sx={{
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
            }}
          >
            <MenuItem value="altitude">
              <Checkbox checked={selectedMetrics.indexOf('altitude') > -1} />
              <ListItemText primary={chartLabels.elevationLabel} />
            </MenuItem>
            <MenuItem value="speed">
              <Checkbox checked={selectedMetrics.indexOf('speed') > -1} />
              <ListItemText primary={chartLabels.speedLabel} />
            </MenuItem>
            <MenuItem value="gradient">
              <Checkbox checked={selectedMetrics.indexOf('gradient') > -1} />
              <ListItemText primary={chartLabels.gradientLabel} />
            </MenuItem>
            <MenuItem value="power">
              <Checkbox checked={selectedMetrics.indexOf('power') > -1} />
              <ListItemText primary={chartLabels.powerLabel} />
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Interactive Chart */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.grey[800],
          borderRadius: '8px'
        }}
      >
        <Box mb={2} p={2} sx={{ backgroundColor: colors.primary[900], borderRadius: 1, border: `1px solid ${colors.primary[700]}` }}>
          <Typography variant="body2" color={colors.grey[100]} fontWeight="bold">
            {selectionStep === 0 && `👆 ${t('clickToStartSelectionGpx', 'Click on the chart to start selecting a range')}`}
            {selectionStep === 1 && `✅ ${t('startPointSetGpx', 'Start point set! Click again to set the end point')}`}
            {selectionStep === 2 && `✓ ${t('selectedRangeGpx', {
              start: startDistance.toFixed(2),
              end: endDistance.toFixed(2),
              unit: units === 'imperial' ? 'mi' : 'km',
              distance: distance.toFixed(2),
              defaultValue: 'Selected: {{start}} to {{end}} {{unit}} ({{distance}} {{unit}})'
            })}`}
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            onClick={handleChartClick}
            style={{ cursor: 'crosshair' }}
            key={`${startDistance}-${endDistance}`}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
            <XAxis
              dataKey="distance"
              stroke={colors.grey[100]}
              label={{ value: chartLabels.distanceLabel, position: 'insideBottom', offset: -5, fill: colors.grey[300] }}
              tickFormatter={(value) => `${value.toFixed(units === 'imperial' ? 2 : 1)}`}
            />
            <YAxis
              stroke={colors.grey[100]}
              label={{
                value: t('valuesMetricLabel', 'Values'),
                angle: -90,
                position: 'insideLeft',
                fill: colors.grey[300]
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.grey[900],
                border: `1px solid ${colors.grey[700]}`,
                borderRadius: '4px'
              }}
              labelStyle={{ color: colors.grey[100] }}
              itemStyle={{ color: colors.blueAccent[400] }}
            />
            <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="top" height={50} />

            {/* Selection Markers */}
            {selectionStep >= 1 && (
              <ReferenceLine
                x={startDistance}
                stroke={colors.greenAccent[500]}
                strokeWidth={3}
                label={{ value: t('startLabel', 'Start'), position: 'top', fill: colors.greenAccent[500], fontSize: 14, fontWeight: 'bold' }}
              />
            )}
            {selectionStep === 2 && (
              <>
                <ReferenceArea
                  x1={startDistance}
                  x2={endDistance}
                  strokeOpacity={0.3}
                  fill={colors.blueAccent[600]}
                  fillOpacity={0.3}
                />
                <ReferenceLine
                  x={endDistance}
                  stroke={colors.redAccent[500]}
                  strokeWidth={3}
                  label={{ value: t('endLabel', 'End'), position: 'top', fill: colors.redAccent[500], fontSize: 14, fontWeight: 'bold' }}
                />
              </>
            )}

            {selectedMetrics.includes('altitude') && (
              <Line
                type="natural"
                dataKey="altitude"
                stroke={colors.chartColors[1]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.elevationLabel}
              />
            )}
            {selectedMetrics.includes('speed') && (
              <Line
                type="natural"
                dataKey="speed"
                stroke={colors.chartColors[2]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.speedLabel}
              />
            )}
            {selectedMetrics.includes('gradient') && (
              <Line
                type="natural"
                dataKey="gradient"
                stroke={colors.chartColors[3]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.gradientLabel}
              />
            )}
            {selectedMetrics.includes('power') && (
              <Line
                type="natural"
                dataKey="power"
                stroke={colors.chartColors[4]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.powerLabel}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Export Button */}
      <Box display="flex" justifyContent="flex-start">
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportGpx}
          disabled={distance <= 0}
          sx={{
            backgroundColor: colors.greenAccent[600],
            color: colors.grey[100],
            '&:hover': {
              backgroundColor: colors.greenAccent[700]
            },
            '&:disabled': {
              backgroundColor: colors.grey[700],
              color: colors.grey[500]
            }
          }}
        >
          {t('exportGpxSegment', 'Export GPX Segment')}
        </Button>
      </Box>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>{t('howToSelectSegment', 'How to select a segment:')}</strong>
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
          <li dangerouslySetInnerHTML={{ __html: t('gpxMethod1', '<strong>Method 1 (Chart):</strong> Click twice on the chart - first click sets the start point, second click sets the end point. Selection updates immediately.') }}></li>
          <li dangerouslySetInnerHTML={{ __html: t('gpxMethod2', '<strong>Method 2 (Manual Input):</strong> Enter start and end distances in the input fields, then click the "Apply" button (or press Enter) to update the chart selection.') }}></li>
          <li>{t('gpxOrangeBorder', 'The input fields will have an orange border when you have unapplied changes.')}</li>
          <li>{t('gpxResetInfo', 'Click "Reset to Full Route" to select the entire route.')}</li>
          <li>{t('gpxHighlightInfo', 'The selected segment will be highlighted in blue on the chart with green (start) and red (end) markers.')}</li>
        </Typography>
      </Alert>
    </Box>
  );
};

export default GpxExportAction;
